import { intersection } from 'lodash'
import { FIRM_ROLE, GROUP_ROLE, INTERNAL_ROLE } from '../router/roles.ts'

type Permissions = {
  firmRole: string[]
  groupRole: string[]
  deniedGroupRole: string[]
  internalRole: string[]
  customRule?: boolean
}

export const canCreateIdeaPermissions: Permissions = {
  firmRole: [FIRM_ROLE.ROLE_NORMAL],
  deniedGroupRole: [GROUP_ROLE.ROLE_MANAGER],
  groupRole: [GROUP_ROLE.ROLE_CAPTAIN, GROUP_ROLE.ROLE_CONTRIBUTOR],
  internalRole: [INTERNAL_ROLE.ROLE_NORMAL],
}

export const canSeeViewStatistics: Permissions = {
  firmRole: [FIRM_ROLE.ROLE_NORMAL, FIRM_ROLE.ROLE_COMPLIANCE],
  groupRole: [GROUP_ROLE.ROLE_CAPTAIN, GROUP_ROLE.ROLE_MANAGER],
  deniedGroupRole: [GROUP_ROLE.ROLE_CONTRIBUTOR],
  internalRole: [
    INTERNAL_ROLE.ROLE_MASTER,
    INTERNAL_ROLE.ROLE_ADMIN,
    INTERNAL_ROLE.ROLE_VIEWONLY,
    INTERNAL_ROLE.ROLE_MASTER,
  ],
}

export const canSeeFirmSelectPermissions: Permissions = {
  firmRole: [],
  groupRole: [],
  deniedGroupRole: [
    GROUP_ROLE.ROLE_CAPTAIN,
    GROUP_ROLE.ROLE_MANAGER,
    GROUP_ROLE.ROLE_CONTRIBUTOR,
  ],
  internalRole: [
    INTERNAL_ROLE.ROLE_MASTER,
    INTERNAL_ROLE.ROLE_ADMIN,
    INTERNAL_ROLE.ROLE_VIEWONLY,
  ],
}

export const canSeeCategorySelectPermissions = ({
  groupId,
  groupRole,
}: {
  groupId: string
  groupRole: string
}): Permissions => {
  const isNotGroupManager = !(
    groupRole === GROUP_ROLE.ROLE_MANAGER && groupId !== ''
  )

  return {
    firmRole: [FIRM_ROLE.ROLE_COMPLIANCE, FIRM_ROLE.ROLE_NORMAL],
    groupRole: [GROUP_ROLE.ROLE_MANAGER],
    deniedGroupRole: [GROUP_ROLE.ROLE_CAPTAIN, GROUP_ROLE.ROLE_CONTRIBUTOR],
    internalRole: [
      INTERNAL_ROLE.ROLE_MASTER,
      INTERNAL_ROLE.ROLE_ADMIN,
      INTERNAL_ROLE.ROLE_VIEWONLY,
    ],
    customRule: isNotGroupManager,
  }
}

export const canSeeContributorSelectPermissions: Permissions = {
  firmRole: [FIRM_ROLE.ROLE_NORMAL, FIRM_ROLE.ROLE_COMPLIANCE],
  groupRole: [GROUP_ROLE.ROLE_CAPTAIN, GROUP_ROLE.ROLE_MANAGER],
  deniedGroupRole: [GROUP_ROLE.ROLE_CONTRIBUTOR],
  internalRole: [
    INTERNAL_ROLE.ROLE_MASTER,
    INTERNAL_ROLE.ROLE_ADMIN,
    INTERNAL_ROLE.ROLE_VIEWONLY,
  ],
}

export const canCreateUserPermissions: Permissions = {
  firmRole: [],
  deniedGroupRole: [],
  groupRole: [],
  internalRole: [INTERNAL_ROLE.ROLE_MASTER, INTERNAL_ROLE.ROLE_ADMIN],
}

// Mirrors the dashboard route access rules (see router-object.tsx `dashboard`) (Manager can access dashboard, but not the side menu item)
// Side menu Dashboard: only firmRole = Normal and groupRole is not Manager
export const canAccessSideMenuDashboardPermissions: Permissions = {
  firmRole: [FIRM_ROLE.ROLE_NORMAL],
  groupRole: [GROUP_ROLE.ROLE_CAPTAIN, GROUP_ROLE.ROLE_CONTRIBUTOR],
  deniedGroupRole: [GROUP_ROLE.ROLE_MANAGER],
  internalRole: [],
}

export function hasPermission(
  permissions: Permissions,
  firmRole: string,
  groupRole: string,
  role: string
): boolean {
  const matchedAllowedFirmRoles = intersection(permissions.firmRole, [firmRole])
  const matchedAllowedGroupRoles = intersection(permissions.groupRole, [
    groupRole,
  ])
  const matchedDeniedGroupRoles = intersection(permissions.deniedGroupRole, [
    groupRole,
  ])
  const matchedAllowedInternalRoles = intersection(permissions.internalRole, [
    role,
  ])

  const customRule = permissions?.customRule ?? true

  return (
    (matchedAllowedFirmRoles.length > 0 &&
      matchedAllowedGroupRoles.length > 0 &&
      matchedDeniedGroupRoles.length === 0 &&
      customRule) ||
    matchedAllowedInternalRoles.length > 0 ||
    matchedAllowedFirmRoles.includes('Compliance')
  )
}

export const blockedFirmIdsCreateEditIdea = [
  '8b632c07-b230-421b-9c45-248649b2d152', // bamlDevFirmId
  'dc5b8230-d1c8-4e0b-bdfa-9ee15d7442f6', // msDevFirmId
  '12506369-520f-452b-b290-c557a1b3afa5', // gsDevFirmId
  '9cedae9c-7aee-40a5-b1ec-e4cb6f9bfa70', // bamlProdFirmId
  '7c3c14e9-a948-4d14-a762-05f290b0134e', // msProdFirmId
  '08c7124e-3027-4c1d-99e1-96cff19aeea4', // gsProdFirmId
  '879172c0-a8bd-428a-b996-8346d6bb5ebb', // MAZA TEST
]

export function hasPermissionAndFirmCheck(
  permissions: Permissions,
  firmId: string,
  firmRole: string,
  groupRole: string,
  role: string
): boolean {
  const matchedAllowedFirmRoles = intersection(permissions.firmRole, [firmRole])
  const matchedAllowedGroupRoles = intersection(permissions.groupRole, [
    groupRole,
  ])
  const matchedDeniedGroupRoles = intersection(permissions.deniedGroupRole, [
    groupRole,
  ])
  const matchedAllowedInternalRoles = intersection(permissions.internalRole, [
    role,
  ])

  if (blockedFirmIdsCreateEditIdea.includes(firmId)) {
    return false
  }

  return (
    (matchedAllowedFirmRoles.length > 0 &&
      matchedAllowedGroupRoles.length > 0 &&
      matchedDeniedGroupRoles.length === 0) ||
    matchedAllowedInternalRoles.length > 0 ||
    matchedAllowedFirmRoles.includes('Compliance')
  )
}
